import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { describePublishBlockReason, type OfferingPublishBlockReason } from '@/lib/publishRestrictions';

interface PublishRestrictionNoticeProps {
  reason: OfferingPublishBlockReason;
  /** The offering's own type, when known — lets the copy name it specifically. */
  offeringType?: string;
  className?: string;
}

/**
 * F15 — amber, informative-not-punitive notice shown when a teacher's current
 * subscription tier blocks publishing an offering (either its type isn't
 * allowed, or the plan's published-offering limit is reached). Matches the
 * warning-state treatment established in TeacherSubscriptionPage.tsx (slate
 * neutrals, amber for warnings, rounded-2xl, border-slate-200) rather than
 * introducing a new palette.
 */
export function PublishRestrictionNotice({ reason, offeringType, className }: PublishRestrictionNoticeProps) {
  const { heading, body } = describePublishBlockReason(reason, offeringType);
  return (
    <div role="alert" className={cn('rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5', className)}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0" aria-hidden="true">
          <Lock className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900">{heading}</p>
          <p className="text-sm text-amber-800 mt-1 leading-relaxed">{body}</p>
          <Button
            asChild
            size="sm"
            className="mt-3 cursor-pointer bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-200"
          >
            <Link to="/teacher-subscription">Manage subscription</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
